import WidgetModel, { SendDataConfig } from '@mm/state/model/WidgetModel';
import { useViewActions } from '@mm/state/actions/viewActions';
import { StoreState } from '@mm/state/store-interfaces';
import React, { ReactNode, useEffect } from 'react';
import classNames from 'classnames';
import { Handle, Position, NodeProps } from 'react-flow-renderer';
import { useSelector } from 'react-redux';
import common from '../index.module.css';
import css from './Integration.module.css';
import { Count } from '../elements/Count/Count';
import { useOpenOnReady } from '../use-open-on-ready';
import { uniqueId } from 'lodash';
import { IntegrationIcon } from '../../../../../Integrations/IntegrationIcon/IntegrationIcon';
import { humanizedIntegrationNames } from '../../../../../Dashboard/ActivationBlock/use-activation-steps';
import { useIntegrationWidget } from './use-integration-widget';
import { selectConnectionWidgetsByType } from '@mm/state/modules/connectionsTableState/selectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { ReactComponent as UsersIcon } from './users-icon.svg';
import { Status } from '@mm/components';
import ConnectionModel from '@mm/state/model/ConnectionModel';

type Props = { className?: string } & NodeProps<{
  widget: WidgetModel;
}>;

export const Integration: React.FC<Props> = ({
  className,
  data,
  isDragging,
  children,
  type,
}) => {
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const widgets = useSelector((state: StoreState) => state.widget.entities);
  const widget = widgets.find(w => w.id === data.widget.id) || data.widget;
  const config = widget.config as Partial<SendDataConfig>;
  const {
    fetchConnections,
    onAddNewConnection,
    onConnectionChange,
    onEditConnection,
  } = useIntegrationWidget(type, widget);
  const { connectionWebhookId } = config;

  const viewActions = useViewActions();
  const connections = useSelector((state: StoreState) =>
    selectConnectionWidgetsByType(state, type),
  );

  const humanizedIntegrationName = humanizedIntegrationNames[type];

  const connection: ConnectionModel = connections?.find(
    c => c.id === connectionWebhookId,
  );

  const onClick = () => {
    if (isDragging) {
      return;
    }

    if (connections?.length) {
      viewActions.showSlideoutMenu('connectionsEditor', {
        widgetId: data.widget.id,
        title: `${humanizedIntegrationName} Integration`,
        selector: (state: StoreState) =>
          selectConnectionWidgetsByType(state, type),
        onConnectionChange,
        onAddNewConnection,
        onEditConnection,
        type,
      });
    } else {
      onAddNewConnection();
    }
  };

  useOpenOnReady(widget, onClick);

  useEffect(() => {
    if (activeFbPageId) {
      fetchConnections();
    }
  }, [activeFbPageId]);

  return (
    <>
      <Handle type="target" position={Position.Top} />
      <div
        className={classNames(className, common.card, css.container)}
        onClick={onClick}
      >
        <div className={css.body}>
          <div className={css.header}>
            <div className={css.icon}>
              <IntegrationIcon integrationType={type} isActive />
            </div>

            <div>
              {React.Children.map<ReactNode, ReactNode>(children, child => {
                if (React.isValidElement(child)) {
                  return React.cloneElement(child, {
                    // @ts-ignore onEdit does not seemto be present or types are off in @types/react
                    onEdit: onClick,
                    key: uniqueId(),
                  });
                }
              })}
            </div>

            <span className={css.placeholder}>
              {humanizedIntegrationName} Integration{' '}
            </span>
          </div>
          {connection && (
            <div className={css.info}>
              <span className={css.connectionName}>{connection.name}</span>

              <div className={css.infoRow}>
                <Count
                  className={css.firesCount}
                  icon={<UsersIcon />}
                  value={`${data.widget.config?.firesCount || 0} sent`}
                />
                <Status status={connection.active ? 'active' : 'inactive'} />
              </div>
            </div>
          )}
        </div>
      </div>

      <Handle type="source" position={Position.Bottom} />
    </>
  );
};
